import { useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { useSelector } from 'react-redux';
import config from 'constants/apiConfig';
import { selectGroupGuid } from 'slices/commonSelectors';
import {
  hwcgContactListKeys,
  toastHwcgLoginSummary,
  type HwcgLoginSummary,
} from 'hooks/hwcgContactLists/hwcgContactListHooks';
import { hwcgMemberCompanyKeys } from 'hooks/hwcgMemberCompanies/hwcgMemberCompanyHooks';

export type DirectoryImportKind = 'contacts' | 'companies';

export type DirectoryImportCell = string | number | boolean | null;
export type DirectoryImportRawRow = Record<string, DirectoryImportCell>;

export type DirectoryImportSheet = {
  name: string;
  headers: string[];
  rowCount: number;
  rows: DirectoryImportRawRow[];
  suggestedMapping: Record<string, string>;
};

export type DirectoryImportParse = {
  fileName: string;
  maxRows: number;
  sheets: DirectoryImportSheet[];
};

export type DirectoryImportStatus =
  | 'new'
  | 'update'
  | 'invalid'
  | 'duplicate_in_file';

export type DirectoryImportPreviewRow = {
  rowNumber: number;
  status: DirectoryImportStatus;
  fields: Record<string, string | boolean | null>;
  errors: string[];
};

export type DirectoryImportPreview = {
  rows: DirectoryImportPreviewRow[];
  counts: Record<DirectoryImportStatus, number>;
};

export type DirectoryImportResult = {
  created: number;
  updated: number;
  skipped: number;
  /** Contacts only: logins created for the imported people. */
  logins?: HwcgLoginSummary;
};

export type DirectoryImportRequest = {
  rows: DirectoryImportRawRow[];
  mapping: Record<string, string>;
  /** Contacts only: the list the rows go into. */
  constituency?: string;
};

function baseUrl(kind: DirectoryImportKind): string {
  return kind === 'contacts'
    ? config.apiGateway.hwcgContactLists
    : config.apiGateway.hwcgMemberCompanies;
}

export function directoryImportError(error: unknown): string | null {
  if (!error) return null;
  const data = (
    error as { response?: { data?: { error?: unknown; message?: unknown } } }
  )?.response?.data;
  const message = data?.error ?? data?.message;
  return typeof message === 'string' && message.trim()
    ? message
    : 'Something went wrong. Try again.';
}

/** Keeps only the mapped columns so previews and commits stay small. */
export function mappedRows(
  rows: DirectoryImportRawRow[],
  mapping: Record<string, string>
): DirectoryImportRawRow[] {
  const columns = Array.from(new Set(Object.values(mapping)));
  return rows.map((row) =>
    Object.fromEntries(columns.map((column) => [column, row[column] ?? null]))
  );
}

export function useParseDirectoryFile(kind: DirectoryImportKind) {
  const groupGuid = useSelector(selectGroupGuid) ?? '';
  return useMutation({
    mutationFn: async (file: File) => {
      const body = new FormData();
      body.append('groupGuid', groupGuid);
      body.append('file', file);
      const response = await axios.post<DirectoryImportParse>(
        `${baseUrl(kind)}/import/parse`,
        body
      );
      return response.data;
    },
  });
}

export function usePreviewDirectoryImport(kind: DirectoryImportKind) {
  const groupGuid = useSelector(selectGroupGuid) ?? '';
  return useMutation({
    mutationFn: async (request: DirectoryImportRequest) => {
      const response = await axios.post<DirectoryImportPreview>(
        `${baseUrl(kind)}/import/preview`,
        { ...request, groupGuid }
      );
      return response.data;
    },
  });
}

export function useCommitDirectoryImport(kind: DirectoryImportKind) {
  const groupGuid = useSelector(selectGroupGuid) ?? '';
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (request: DirectoryImportRequest) => {
      const response = await axios.post<DirectoryImportResult>(
        `${baseUrl(kind)}/import/commit`,
        { ...request, groupGuid }
      );
      return response.data;
    },
    onSuccess: (result) => {
      toastHwcgLoginSummary(result.logins);
      void queryClient.invalidateQueries({
        queryKey:
          kind === 'contacts'
            ? hwcgContactListKeys.list(groupGuid)
            : hwcgMemberCompanyKeys.list(groupGuid),
      });
    },
  });
}

export async function downloadDirectoryImportTemplate(
  kind: DirectoryImportKind
) {
  const { data } = await axios.get<Blob>(
    `${baseUrl(kind)}/import/template.csv`,
    { responseType: 'blob' }
  );
  const url = URL.createObjectURL(data);
  const link = document.createElement('a');
  link.href = url;
  link.download =
    kind === 'contacts'
      ? 'hwcg-contact-import-template.csv'
      : 'hwcg-member-company-import-template.csv';
  link.click();
  URL.revokeObjectURL(url);
}
